/**
 * Easing shared by playback speed, slow-motion and camera moves (Sprint 2). Frame-rate independent: every function
 * takes the real time step, so 30 fps and 120 fps converge alike. (The quintic ease-in-out for fixed-duration moves
 * is `easeInOut` in scaleProfiles.ts.)
 */

/** Fraction of the remaining distance covered in `dt` by exponential smoothing with time constant `tau` (s). */
export function smoothingFactor(dt: number, tau: number): number {
  if (tau <= 0) return 1;
  return 1 - Math.exp(-Math.max(dt, 0) / tau);
}

/**
 * Moves `current` towards `target` in log space (for quantities spanning orders of magnitude, like playback speeds
 * from 1 h/s to 1 yr/s: 1 h/s → 1 d/s takes as long as 1 d/s → 24 d/s). Both must be > 0; snaps when within 0.1 %.
 */
export function approachLog(current: number, target: number, dt: number, tau: number): number {
  if (!(current > 0) || !(target > 0)) return target;
  const next = Math.exp(Math.log(current) + (Math.log(target) - Math.log(current)) * smoothingFactor(dt, tau));
  return Math.abs(next / target - 1) < 1e-3 ? target : next;
}

/**
 * Critically damped spring step (no overshoot), advancing `state.x` towards `target`. `omega` is the natural
 * frequency (rad/s); about 4/omega seconds to settle. Mutates and returns `state`.
 */
export function springStep(state: { x: number; v: number }, target: number, omega: number, dt: number): typeof state {
  const h = Math.max(dt, 0);
  const e = Math.exp(-omega * h);
  const d = state.x - target;
  const c = state.v + omega * d;
  state.x = target + (d + c * h) * e;
  state.v = (state.v - omega * c * h) * e;
  return state;
}
